export interface PcmAudio {
  channels: Float32Array[];
  sampleRate: number;
}

const fourCc = (view: DataView, offset: number) =>
  String.fromCharCode(
    view.getUint8(offset),
    view.getUint8(offset + 1),
    view.getUint8(offset + 2),
    view.getUint8(offset + 3),
  );

/** Decode the small, repository-controlled PCM16 WAV cues without opening audio hardware. */
export function decodePcm16Wav(data: ArrayBuffer): PcmAudio {
  if (data.byteLength < 12) throw new Error('WAV header is truncated');
  const view = new DataView(data);
  if (fourCc(view, 0) !== 'RIFF' || fourCc(view, 8) !== 'WAVE') {
    throw new Error('WAV is not a RIFF/WAVE file');
  }

  let channels = 0;
  let sampleRate = 0;
  let blockAlign = 0;
  let formatFound = false;
  let samples: Float32Array[] | null = null;
  let offset = 12;
  while (offset + 8 <= data.byteLength) {
    const chunk = fourCc(view, offset);
    const size = view.getUint32(offset + 4, true);
    const start = offset + 8;
    const end = start + size;
    if (end > data.byteLength) throw new Error(`WAV ${chunk} chunk is truncated`);

    if (chunk === 'fmt ') {
      if (size < 16) throw new Error('WAV fmt chunk is truncated');
      const format = view.getUint16(start, true);
      channels = view.getUint16(start + 2, true);
      sampleRate = view.getUint32(start + 4, true);
      blockAlign = view.getUint16(start + 12, true);
      const bitsPerSample = view.getUint16(start + 14, true);
      if (format !== 1 || channels === 0 || sampleRate === 0 || bitsPerSample !== 16) {
        throw new Error('WAV must be PCM16');
      }
      if (blockAlign !== channels * 2) throw new Error('WAV PCM16 block alignment is invalid');
      formatFound = true;
    } else if (chunk === 'data') {
      if (!formatFound) throw new Error('WAV data appears before fmt');
      if (size % blockAlign !== 0) throw new Error('WAV PCM16 data is truncated');
      const frames = size / blockAlign;
      samples = Array.from({ length: channels }, () => new Float32Array(frames));
      for (let frame = 0; frame < frames; frame++) {
        for (let channel = 0; channel < channels; channel++) {
          samples[channel]![frame] =
            view.getInt16(start + (frame * channels + channel) * 2, true) / 32_768;
        }
      }
    }

    offset = end + (size & 1);
    if (offset > data.byteLength) throw new Error(`WAV ${chunk} chunk padding is truncated`);
  }
  if (!formatFound || !samples) throw new Error('WAV fmt or data chunk is missing');
  return { channels: samples, sampleRate };
}
